import { Accordion } from '@insify/design-system/components';

import { YesOrNo } from '../YesOrNo';

import type { YesOrNoProps } from '../YesOrNo';

type SubmitDeclarations<PropertyDeclaration extends string> = (params: {
  consent: boolean;
  declarations: PropertyDeclaration[];
}) => void;

type PropertyDeclarationsProps<PropertyDeclaration extends string> = Omit<
  YesOrNoProps,
  'trackingId' | 'children' | 'eventHandlers'
> & {
  translations: YesOrNoProps['translations'] & {
    [PropertyAccordionHeader in PropertyDeclaration as `header${Capitalize<PropertyDeclaration>}`]: React.ReactNode;
  } & {
    [PropertyAccordionDetails in PropertyDeclaration as `details${Capitalize<PropertyDeclaration>}`]?: React.ReactNode;
  };
  icons: {
    [PropertyIcon in PropertyDeclaration]: React.ReactElement;
  };
  declarations: {
    [Declaration in PropertyDeclaration]?: boolean;
  };
  eventHandlers: Omit<YesOrNoProps['eventHandlers'], 'onSubmit'> & {
    onSubmit: SubmitDeclarations<PropertyDeclaration>;
  };
};

export const PropertyDeclarations = <PropertyDeclaration extends string>({
  declarations,
  translations,
  ...props
}: PropertyDeclarationsProps<PropertyDeclaration>) => {
  const declarationsKeys = Object.entries(declarations)
    .filter(([_key, value]) => !!value)
    .map(([key]) => key) as PropertyDeclaration[];

  const submitDeclarations = (answer: boolean) => {
    props.eventHandlers.onSubmit({ consent: answer, declarations: declarationsKeys });
  };

  return (
    <YesOrNo
      {...props}
      translations={translations}
      eventHandlers={{ ...props.eventHandlers, onSubmit: submitDeclarations }}
    >
      {declarationsKeys.map((declarationKey) => (
        <Accordion
          title={translations[`${declarationKey}Header` as keyof typeof translations] as string}
          key={declarationKey}
          trackingId={declarationKey}
        />
      ))}
    </YesOrNo>
  );
};
